import type { ActionExecutionContext, ActionExecutionResult } from '../types';
import { semanticPortContext, type SemanticPortResult } from '../ports';
import { MissingSemanticComponentCapabilityError } from '../component-capability-registry';

export function readString<T extends object>(config: T, ...keys: string[]): string | undefined {
  for (const key of keys) {
    const value = config[key as keyof T];
    if (typeof value === 'string' && value.trim() !== '') {
      return value;
    }
  }
  return undefined;
}

export function intentValue(ctx: ActionExecutionContext): unknown {
  return ctx.intentEnvelope?.payload.params.value;
}

export async function invokeSemanticPort(
  ctx: ActionExecutionContext,
  capability: string,
  invoke:
    | ((
        context: ReturnType<typeof semanticPortContext>
      ) => Promise<SemanticPortResult | void> | SemanticPortResult | void)
    | undefined
): Promise<ActionExecutionResult> {
  if (!invoke) {
    ctx.logger.warn(`${capability} unsupported: host capability is not wired`, {
      stepId: ctx.stepId,
      actionId: ctx.action.id,
    });
    return {
      status: 'unsupported',
      unsupported: true,
      reason: `missing_${capability}_capability`,
    };
  }

  try {
    const result = await invoke(semanticPortContext(ctx));
    if (result?.resultOwner === 'destination') {
      return {
        status: 'deferred',
        reason: 'result_owned_by_destination',
        resultParams: result.resultParams,
        navigated: result.navigated,
      };
    }
    return {
      status: 'succeeded',
      resultParams: result?.resultParams,
      navigated: result?.navigated,
    };
  } catch (error) {
    if (error instanceof MissingSemanticComponentCapabilityError) {
      return {
        status: 'unsupported',
        unsupported: true,
        reason: `missing_${capability}_capability`,
      };
    }
    ctx.logger.error(
      `${capability} failed at its owning capability`,
      error instanceof Error ? error : undefined,
      { stepId: ctx.stepId, actionId: ctx.action.id }
    );
    return {
      status: 'failed',
      reason: `${capability}_failed`,
    };
  }
}
